import { Alert, Box, TextField, Typography, paletteColors } from '@evaneos/b2b-ui';
import { ChangeEvent, FormEvent, useState } from 'react';

import { useIntl } from 'react-intl';

import { useUpdateRequesterInformationMutation } from '../../../../resources/dossier/mutations';
import { DossierId } from '../../../../resources/dossier/types';
import { EditableTrigger } from '../../../../design-system/DefinitionList/EditableTrigger';
import { VisuallyHidden } from '../../../../shared/components/VisuallyHidden';
import { useGetTrackedTripProperties, useTrackEventAmplitude } from '../../../../shared/analytics';

type RequesterNameEditableItemProps = {
    requesterName: string;
    requesterPhone: string;
    dossierId: DossierId;
};

export function RequesterNameEditableItem({
    requesterName,
    requesterPhone,
    dossierId,
}: RequesterNameEditableItemProps) {
    const { formatMessage } = useIntl();
    const [nameValue, setNameValue] = useState<string>(requesterName);
    const [hasNameError, setHasNameError] = useState(false);
    const updateRequesterInformationMutation = useUpdateRequesterInformationMutation(dossierId);
    const { trackEvent } = useTrackEventAmplitude();
    const trackedTripProperties = useGetTrackedTripProperties(dossierId);

    const labelId = 'traveller-name-label';
    // Extremely permissive: allows letters (all languages), numbers, spaces, and common punctuation/symbols
    // Includes: accented chars, CJK characters, Arabic, Cyrillic, and most printable symbols
    const validNameCharacters =
        '[- a-zA-ZÀ-ÿ\\u00f1\\u00d10-9,:()\\[\\].\'"&@#/*+=_{}|;!?<>~`$%^€£¥§°\\u0080-\\uFFFF]*$';

    const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;

        if (!e.target.checkValidity()) {
            setHasNameError(true);
        } else {
            setHasNameError(false);
        }

        setNameValue(inputValue);
    };

    const handleBeforeClose = () => {
        const trimmedName = nameValue.trim();

        // Allow closing without validation if the name hasn't been modified
        if (trimmedName === requesterName) {
            setHasNameError(false);
            return true;
        }

        // Validate name is not empty
        if (trimmedName === '') {
            setHasNameError(true);
            return false;
        }

        // Validate name contains only valid characters
        const namePattern = new RegExp(`^${validNameCharacters}`);
        if (!namePattern.test(trimmedName)) {
            setHasNameError(true);
            return false;
        }

        if (!updateRequesterInformationMutation.isPending) {
            updateRequesterInformationMutation.mutate({
                name: trimmedName,
                phoneNumber: requesterPhone,
            });

            trackEvent((ampli, defaultProperties) => {
                ampli.travelerInformationChanged({
                    ...defaultProperties,
                    ...trackedTripProperties,
                    edited_field: 'name',
                });
            });
        }

        setHasNameError(false);
        return true;
    };

    const handleSubmit = (e: FormEvent<HTMLFormElement>, close: () => void) => {
        e.preventDefault();
        close();
    };

    return (
        <EditableTrigger
            labelId={labelId}
            placement="over"
            onBeforeClose={handleBeforeClose}
            popoverWidth="trigger"
            triggerSx={{
                display: 'block',
                minWidth: 300,
                maxWidth: '100%',
                textAlign: 'left',
                padding: 0.5,
                ml: -0.5,
                borderRadius: 1,
                '&:hover': { backgroundColor: paletteColors.neutral[100] },
                '&:focus-visible': { bgcolor: paletteColors.neutral[100] },
            }}
            editor={({ close }) => (
                <Box component="form" position="relative" onSubmit={(e) => handleSubmit(e, close)}>
                    <VisuallyHidden id={labelId}>
                        {formatMessage({
                            id: 'dossier.information.traveller.name',
                        })}
                    </VisuallyHidden>

                    <TextField
                        name="requester-name"
                        type="text"
                        error={hasNameError}
                        slotProps={{
                            htmlInput: {
                                'data-testid': 'input-requester-name',
                                'aria-labelledby': labelId,
                                'aria-invalid': hasNameError,
                                'aria-describedby': hasNameError ? 'name-error' : undefined,
                                pattern: validNameCharacters,
                            },
                        }}
                        size="small"
                        defaultValue={requesterName}
                        onChange={handleNameChange}
                        fullWidth
                        autoFocus
                    />
                    {hasNameError && (
                        <Alert
                            id="name-error"
                            severity="error"
                            sx={{ position: 'absolute', top: '110%', left: 0, right: 0 }}
                        >
                            <Typography variant="body2" color="error.700">
                                {nameValue.trim() === ''
                                    ? formatMessage({
                                          id: 'dossier.information.traveller.update.error.empty',
                                      })
                                    : formatMessage({
                                          id: 'dossier.information.traveller.update.error.invalid_characters',
                                      })}
                            </Typography>
                        </Alert>
                    )}
                </Box>
            )}
        >
            <Typography
                variant="h2"
                component="span"
                sx={{
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}
            >
                {requesterName}
            </Typography>
        </EditableTrigger>
    );
}
